import { acceptHMRUpdate, defineStore } from "pinia"
import { markRaw } from "vue"
import { api } from "@/logic/api"
import { getConstants } from "@/logic/constants"
import audioPeaksWorkletUrl from "./audioPeaksWorklet.js?url"
import { assembleRecording, type RecordingBlock, type RecordingPiece, type RecordingSegment, RecordingSplitter } from "./recordingPieces"
import { CutReencoder, needsReencodedCuts } from "./reencodedCuts"
import { forgetSavedRecording, loadSavedRecording, lockRecording, releaseRecordingLock, saveRecording, savedRecordingDuration } from "./savedRecording"

type Microphone = { id: string; name: string }

export const MAX_RECORDING_SECONDS = 90 * 60
// Chrome, Edge, Firefox and Safari record this format, and recordingPieces.ts splits it
const RECORDING_MIME_TYPE = "video/webm;codecs=vp8,opus"
// The recording is split into pieces as it arrives, so little is left to upload when it stops
const CHUNK_MILLISECONDS = 1000
// The video can only be cut at keyframes, see recordingPieces.ts
const KEYFRAME_INTERVAL_MILLISECONDS = 1000
const COUNTDOWN_SECONDS = 3
// Other errors, mostly NotReadableError, mean that the system could not start the microphone, e.g. because another app uses it
const MICROPHONE_ERRORS: Record<string, string> = {
  NotAllowedError: "media-libraries.screenRecording.microphoneDenied",
  NotFoundError: "media-libraries.screenRecording.microphoneNotFound",
}

/**
 * Returns what the recorder needs but this browser does not have, e.g. mobile browsers cannot share the screen
 * and older Safari versions cannot record WebM.
 */
function missingBrowserFeatures() {
  const features = {
    getDisplayMedia: !!navigator.mediaDevices?.getDisplayMedia,
    "MediaRecorder with WebM": typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(RECORDING_MIME_TYPE),
    AudioWorklet: typeof AudioWorkletNode !== "undefined",
    "Web Locks": !!navigator.locks,
    // Only needed to cut recordings with few keyframes, see reencodedCuts.ts
    WebCodecs: !needsReencodedCuts || (typeof VideoEncoder !== "undefined" && typeof VideoDecoder !== "undefined"),
  }
  return Object.keys(features).filter((feature) => !features[feature as keyof typeof features])
}

/**
 * Browsers ignore a microphone that is only preferred and record the system's default, so the chosen one is required.
 * Once it was unplugged, the system's default is recorded instead, see listMicrophones.
 */
async function requestMicrophone(microphoneId: string) {
  if (!microphoneId) {
    return navigator.mediaDevices.getUserMedia({ audio: true })
  }
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: microphoneId } } })
  } catch (error) {
    if ((error as Error).name !== "OverconstrainedError") {
      throw error
    }
    return navigator.mediaDevices.getUserMedia({ audio: true })
  }
}

// ScreenRecordingStorage commits the blocks under the same name
function blockUploadUrl(index: number) {
  const blockId = btoa(String(index).padStart(getConstants().MEDIALIBRARY.SCREEN_RECORDING_BLOCK_ID_DIGITS, "0"))
  return `${uploadUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`
}

export function formatRecordingTime(seconds: number) {
  const wholeSeconds = Math.floor(seconds)
  return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, "0")}`
}

// The browser objects and the growing data of the current recording, kept outside the store state so Vue does not track them
let recordingId: string
let mediaRecorder: MediaRecorder
let audioPeaks: number[] = []
// Mixes the audio of the recording, see beginRecording
let audioPeaksNode: AudioWorkletNode
// Stops waiting for a lost microphone to be plugged in again, see reconnectMicrophone
let deviceChangeListener: AbortController | undefined
let countdownInterval: ReturnType<typeof setInterval> | undefined
let maxDurationTimeout: ReturnType<typeof setTimeout> | undefined
let chunks: Blob[] = []
let splitter: RecordingSplitter
let pieces: RecordingPiece[] = []
// The chunks are split one after another, because a piece can continue in the next chunk
let splitQueue: Promise<void> = Promise.resolve()
let pendingBlocks: RecordingBlock[] = []
let uploadQueue: Promise<void> = Promise.resolve()
// The link that the blocks of the recording are uploaded to, which is requested with the first block, see uploadBlocks
let uploadUrl: string | null = null
let cutReencoder: CutReencoder | null = null
// Counts the recordings of this page, so the work of a recording that was reset in the meantime, e.g. by a logout, does not go on
let session = 0

/**
 * Records the screen, uploads it in pieces while recording, lets the user cut it
 * and then has the server put the kept pieces together into a Bunny video.
 */
export const useScreenRecordingStore = defineStore("screenRecording", {
  state: () => {
    return {
      // setup: the user chooses what to record, countdown: the recording starts after the countdown
      phase: "idle" as "idle" | "setup" | "countdown" | "recording" | "editing" | "processing",
      error: null as string | null,
      // Created directly after the click that opens the setup, because Safari only plays, records and measures audio then
      audioContext: null as AudioContext | null,
      screenStream: null as MediaStream | null,
      // A floating window would be part of the video when the whole screen is recorded
      isRecordingWholeScreen: false,
      recordMicrophone: false,
      microphoneStream: null as MediaStream | null,
      // The microphones the user can choose from. The browser only names them once the user allowed microphone access.
      microphones: [] as Microphone[],
      // An empty id records the microphone that is set as default in the system
      microphoneId: "",
      // The microphone was unplugged during the recording, which goes on without it until it is plugged in again
      isMicrophoneLost: false,
      // Seconds until the recording starts
      countdown: 0,
      // Also set for a recovered recording, because the file name of the video contains it
      recordingStartedAt: 0,
      duration: 0,
      previewUrl: "",
      // The loudest sample of every 100 ms of the recording, drawn as waveform in the editor
      audioPeaks: [] as number[],
      // The parts of the recording that end up in the video, in seconds and in order
      segments: [] as RecordingSegment[],
      // The times in seconds at which a part of the video can start or end
      cutPoints: [] as number[],
      blockCount: 0,
      uploadedBlockCount: 0,
      // The blocks of the video once the server put it together. Azure has then dropped the pieces that were cut out, so the cut can no longer change.
      cutBlockIndexes: null as number[] | null,
      // The uploader that shows the recording, when a page has several uploaders (e.g. one per language)
      owner: null as symbol | null,
      // A recording that was not finished before the page was reloaded, see refreshSavedRecording
      savedRecording: null as { recordedAt: number; duration: number } | null,
      // The setup then explains that the browser is not supported
      missingFeatures: [] as string[],
    }
  },
  getters: {
    keptDuration(state): number {
      return state.segments.reduce((total, segment) => total + segment.end - segment.start, 0)
    },
    isCutFinal(state): boolean {
      return !!state.cutBlockIndexes
    },
    isCutLocked(): boolean {
      return this.phase === "processing" || this.isCutFinal
    },
  },
  actions: {
    openSetup() {
      this.error = null
      this.missingFeatures = missingBrowserFeatures()
      if (this.missingFeatures.length) {
        // Only for debugging, the setup tells the user that the browser is not supported
        console.warn(`Screen recording is not supported in this browser, which is missing: ${this.missingFeatures.join(", ")}`)
        this.phase = "setup"
        return
      }
      this.audioContext = markRaw(new AudioContext())
      this.phase = "setup"
      if (this.recordMicrophone) {
        void this.enableMicrophone()
      }
    },

    async chooseScreen() {
      let stream: MediaStream
      try {
        const controller = "CaptureController" in window ? new CaptureController() : undefined
        const selection = navigator.mediaDevices.getDisplayMedia({
          video: { width: { max: 1920 }, height: { max: 1080 }, frameRate: { max: 30 } },
          audio: true,
          controller,
        })
        // Chrome brings the chosen tab or window to the front, but the user still sets up and starts the recording here
        controller?.setFocusBehavior("no-focus-change")
        stream = await selection
      } catch (error) {
        // Canceling the selection keeps the earlier choice
        if (!(error instanceof DOMException && error.name === "NotAllowedError")) {
          this.error = "media-libraries.screenRecording.startFailed"
        }
        return
      }
      // The user may have closed the setup while the browser asked what to record
      if (this.phase !== "setup") {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      this.screenStream?.getTracks().forEach((track) => track.stop())
      this.screenStream = markRaw(stream)
      const { displaySurface, width, height } = stream.getVideoTracks()[0].getSettings()
      this.isRecordingWholeScreen = displaySurface === "monitor"
      // Firefox doesn't tell what was chosen, but unlike a window the whole screen has the screen's aspect ratio
      if (!displaySurface) {
        this.isRecordingWholeScreen = Math.abs(width! / height! / (screen.width / screen.height) - 1) < 0.01
      }
      // Chrome shows its own "Stop sharing" button, which ends the recording or, before it started, the choice
      stream.getVideoTracks()[0].onended = () => {
        if (this.phase === "recording") {
          this.stopRecording()
          return
        }
        if (this.phase === "countdown") {
          this.cancelCountdown()
        }
        this.screenStream = null
      }
    },

    async enableMicrophone() {
      this.microphoneStream?.getTracks().forEach((track) => track.stop())
      this.microphoneStream = null
      const requestedMicrophoneId = this.microphoneId
      let stream: MediaStream
      try {
        stream = await requestMicrophone(requestedMicrophoneId)
      } catch (error) {
        this.recordMicrophone = false
        // The user may have closed the setup while the browser asked for access, and the uploader has no use for the error
        if (this.phase === "setup") {
          this.error = MICROPHONE_ERRORS[(error as DOMException).name] ?? "media-libraries.screenRecording.microphoneUnavailable"
        }
        return
      }
      // The user may have turned the microphone off, chosen another one or closed the setup while the browser asked for access
      if (!this.recordMicrophone || this.phase !== "setup" || this.microphoneId !== requestedMicrophoneId) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      this.microphoneStream = markRaw(stream)
      this.error = null
      await this.listMicrophones()
    },

    disableMicrophone() {
      this.microphoneStream?.getTracks().forEach((track) => track.stop())
      this.microphoneStream = null
      this.recordMicrophone = false
    },

    async listMicrophones() {
      const devices = await navigator.mediaDevices.enumerateDevices()
      // Chrome additionally lists the system's default microphone under the ids "default" and "communications"
      this.microphones = devices.filter((device) => device.kind === "audioinput" && !["default", "communications"].includes(device.deviceId)).map((device) => ({ id: device.deviceId, name: device.label }))
      // A microphone that was unplugged can no longer be recorded
      if (!this.microphones.some((microphone) => microphone.id === this.microphoneId)) {
        this.microphoneId = ""
      }
    },

    async startCountdown() {
      try {
        await this.audioContext!.audioWorklet.addModule(audioPeaksWorkletUrl)
      } catch {
        this.stopCapture()
        this.error = "media-libraries.screenRecording.startFailed"
        this.phase = "idle"
        return
      }

      // The user may have closed the setup in the meantime or clicked twice
      if (this.phase !== "setup") {
        return
      }
      // Tells the user when the recording starts, so they know that everything from then on is recorded
      this.countdown = COUNTDOWN_SECONDS
      this.phase = "countdown"
      countdownInterval = setInterval(() => {
        this.countdown--
        if (this.countdown === 0) {
          this.beginRecording()
        }
      }, 1000)
    },

    beginRecording() {
      clearInterval(countdownInterval)
      recordingId = crypto.randomUUID()
      chunks = []
      splitter = new RecordingSplitter()
      // The splitter adds the pieces as it finds them, while a recovered recording brings its own
      pieces = splitter.pieces
      splitQueue = Promise.resolve()
      pendingBlocks = []
      uploadQueue = Promise.resolve()
      uploadUrl = null
      session++
      const recordingSession = session
      this.blockCount = 0
      this.uploadedBlockCount = 0
      this.cutBlockIndexes = null
      // MediaRecorder only records a single audio track, so the shared audio and the microphone are mixed into one.
      // On the way they pass the peaks worklet, which measures them for the waveform. Without any audio the track is silent.
      audioPeaksNode = new AudioWorkletNode(this.audioContext!, "audio-peaks")
      audioPeaks = []
      audioPeaksNode.port.onmessage = (event) => audioPeaks.push(event.data)
      const audioDestination = this.audioContext!.createMediaStreamDestination()
      this.screenStream!.getAudioTracks().forEach((track) => this.audioContext!.createMediaStreamSource(new MediaStream([track])).connect(audioPeaksNode))
      if (this.microphoneStream) {
        this.recordMicrophoneStream(this.microphoneStream)
        deviceChangeListener = new AbortController()
        navigator.mediaDevices.addEventListener("devicechange", () => this.reconnectMicrophone(), { signal: deviceChangeListener.signal })
      }
      audioPeaksNode.connect(audioDestination)
      mediaRecorder = new MediaRecorder(new MediaStream([...this.screenStream!.getVideoTracks(), ...audioDestination.stream.getAudioTracks()]), {
        mimeType: RECORDING_MIME_TYPE,
        videoKeyFrameIntervalDuration: KEYFRAME_INTERVAL_MILLISECONDS,
      })
      mediaRecorder.ondataavailable = (event) => {
        chunks.push(event.data)
        splitQueue = splitQueue.then(async () => {
          const data = new Uint8Array(await event.data.arrayBuffer())
          if (recordingSession === session) {
            this.uploadBlocks(splitter.push(data)).catch(() => {})
          }
        })
      }
      mediaRecorder.onstop = () => this.finishRecording()
      mediaRecorder.start(CHUNK_MILLISECONDS)
      void lockRecording(recordingId)
      this.recordingStartedAt = Date.now()
      maxDurationTimeout = setTimeout(() => this.stopRecording(), MAX_RECORDING_SECONDS * 1000)
      this.phase = "recording"
    },

    // An unplugged microphone ends its track, while the recording goes on without it
    recordMicrophoneStream(stream: MediaStream) {
      this.microphoneStream = markRaw(stream)
      this.isMicrophoneLost = false
      this.audioContext!.createMediaStreamSource(stream).connect(audioPeaksNode)
      const track = stream.getAudioTracks()[0]
      const loseMicrophone = () => {
        this.isMicrophoneLost = true
        // The system's default microphone is replaced by another one right away, which can be recorded instead
        void this.reconnectMicrophone()
      }
      track.onended = loseMicrophone
      // It can also have been unplugged before the recording started
      if (track.readyState === "ended") {
        loseMicrophone()
      }
    },

    // Records the lost microphone again once it is plugged in again. Another one is only recorded when the system's default was chosen, like in the setup.
    async reconnectMicrophone() {
      if (!this.isMicrophoneLost) {
        return
      }
      const recordingSession = session
      const devices = await navigator.mediaDevices.enumerateDevices()
      const isMicrophoneBack = devices.some((device) => device.kind === "audioinput" && (!this.microphoneId || device.deviceId === this.microphoneId))
      if (!isMicrophoneBack) {
        return
      }
      const stream = await requestMicrophone(this.microphoneId)
      // The recording may have stopped in the meantime, or the microphone may have been reconnected by an earlier change of the devices
      if (recordingSession !== session || this.phase !== "recording" || !this.isMicrophoneLost) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      this.recordMicrophoneStream(stream)
    },

    stopRecording() {
      if (mediaRecorder.state === "recording") {
        mediaRecorder.stop()
      }
    },

    // Back in the setup, the user can change what they record before they start again
    cancelCountdown() {
      clearInterval(countdownInterval)
      this.phase = "setup"
    },

    cancelSetup() {
      this.stopCapture()
      // Errors of the setup, e.g. a denied microphone, would otherwise stay in the uploader
      this.error = null
      this.phase = "idle"
    },

    stopCapture() {
      this.screenStream?.getTracks().forEach((track) => track.stop())
      this.screenStream = null
      this.microphoneStream?.getTracks().forEach((track) => track.stop())
      this.microphoneStream = null
      deviceChangeListener?.abort()
      void this.audioContext?.close()
      this.audioContext = null
      clearInterval(countdownInterval)
      clearTimeout(maxDurationTimeout)
    },

    async finishRecording() {
      const recordingSession = session
      this.stopCapture()
      this.duration = (Date.now() - this.recordingStartedAt) / 1000
      this.audioPeaks = audioPeaks
      this.segments = [{ start: 0, end: this.duration }]
      const recording = new Blob(chunks, { type: "video/webm" })
      this.previewUrl = URL.createObjectURL(recording)
      // The editor cuts at the start of pieces, so all pieces have to be split off first
      await splitQueue
      if (recordingSession !== session) {
        return
      }
      this.uploadBlocks(splitter.finish()).catch(() => {})
      if (needsReencodedCuts) {
        // The few keyframes would allow only rough cuts, so the parts around cuts between them are encoded again
        cutReencoder = new CutReencoder(
          recording,
          pieces,
          (blocks) => this.uploadBlocks(blocks),
          () => this.segments,
        )
        this.cutPoints = Array.from({ length: Math.floor(this.duration) + 1 }, (_, second) => second)
      } else {
        this.cutPoints = pieces.filter((piece) => piece.isCutPoint).map((piece) => piece.start / 1000)
      }
      this.phase = "editing"
    },

    /**
     * Uploads the blocks after the ones that are still pending. Callers that don't wait for it ignore a failed upload,
     * because it is retried with the next blocks and again before publishing. It also fails when the recording was
     * handed over, discarded or otherwise reset in the meantime. Its blocks are then neither uploaded, counted nor saved.
     */
    uploadBlocks(blocks: RecordingBlock[]): Promise<void> {
      pendingBlocks.push(...blocks)
      this.blockCount += blocks.length
      const uploadSession = session
      uploadQueue = uploadQueue
        .catch(() => {})
        .then(async () => {
          while (uploadSession === session && pendingBlocks.length) {
            if (!uploadUrl) {
              const requestedUploadUrl = await this.requestUploadUrl()
              if (uploadSession !== session) {
                break
              }
              uploadUrl = requestedUploadUrl
            }
            const response = await fetch(blockUploadUrl(pendingBlocks[0].index), { method: "PUT", body: pendingBlocks[0].data })
            if (uploadSession !== session) {
              break
            }
            if (!response.ok) {
              // The link expires after a day, so the next try gets a new one
              if (response.status === 403) {
                uploadUrl = null
              }
              throw new Error(`Azure did not store the block: ${response.status}`)
            }
            const uploadedIndex = pendingBlocks.shift()!.index
            this.uploadedBlockCount++
            // Blocks are uploaded in order, so all pieces up to this one are on the server
            const uploadedPieces = pieces.filter((piece) => piece.blockIndex <= uploadedIndex)
            if (uploadedPieces.length) {
              saveRecording({ id: recordingId, recordedAt: this.recordingStartedAt, pieces: uploadedPieces, audioPeaks, cutBlockIndexes: this.cutBlockIndexes })
            }
          }
          if (uploadSession !== session) {
            throw new Error("The recording was reset")
          }
        })
      return uploadQueue
    },

    // A link that only allows uploading this recording, see ScreenRecordingStorage
    async requestUploadUrl(): Promise<string> {
      const response: { url: string } = await api.post(`/medialibraries/screen-recordings/${recordingId}/upload-url`)
      return response.url
    },

    /**
     * Continues with the recording that was not finished before the page was reloaded. The server puts the uploaded pieces
     * together into the whole recording, which the editor shows like a new recording.
     */
    async recoverRecording(): Promise<string | null> {
      const savedRecording = loadSavedRecording()
      // Another tab may have finished or discarded the recording in the meantime
      if (!savedRecording) {
        this.savedRecording = null
        return null
      }
      this.error = null
      if (!(await lockRecording(savedRecording.id))) {
        this.error = "media-libraries.screenRecording.recordingInOtherTab"
        return null
      }
      recordingId = savedRecording.id
      this.recordingStartedAt = savedRecording.recordedAt
      pieces = savedRecording.pieces
      audioPeaks = savedRecording.audioPeaks
      pendingBlocks = []
      uploadQueue = Promise.resolve()
      uploadUrl = null
      session++
      const recordingSession = session
      this.blockCount = 0
      this.uploadedBlockCount = 0
      this.cutBlockIndexes = savedRecording.cutBlockIndexes
      this.duration = savedRecordingDuration(savedRecording)
      try {
        // The server put the cut video together before, which dropped the pieces that were cut out, so it is published as it is
        if (this.cutBlockIndexes) {
          const response: { server_id: string } = await api.post(`/medialibraries/screen-recordings/${recordingId}/publish`, { block_indexes: this.cutBlockIndexes })
          if (recordingSession !== session) {
            return null
          }
          return response.server_id
        }
        const { headers, blockIndexes } = assembleRecording(pieces, [{ start: 0, end: this.duration }])
        await this.uploadBlocks(headers)
        const response: { url: string } = await api.post(`/medialibraries/screen-recordings/${recordingId}/preview`, { block_indexes: blockIndexes })
        this.previewUrl = response.url
      } catch {
        // The recording stays saved until the user discards it, because the connection may only have been lost for a moment
        if (recordingSession === session) {
          releaseRecordingLock()
          this.error = "media-libraries.screenRecording.recoveryFailed"
        }
        return null
      }
      if (recordingSession !== session) {
        return null
      }
      this.audioPeaks = audioPeaks
      this.segments = [{ start: 0, end: this.duration }]
      this.cutPoints = pieces.filter((piece) => piece.isCutPoint).map((piece) => piece.start / 1000)
      this.phase = "editing"
      return null
    },

    /**
     * Returns the server id of the Bunny video, which can be saved like an uploaded file.
     * The recording is kept until it was handed over, see ScreenRecording.vue.
     */
    async publishRecording(): Promise<string | null> {
      const recordingSession = session
      this.phase = "processing"
      this.error = null
      try {
        const { headers, blockIndexes } = assembleRecording(pieces, this.segments, await cutReencoder?.prepare(this.segments))
        await this.uploadBlocks(headers)
        this.cutBlockIndexes = blockIndexes
        saveRecording({ id: recordingId, recordedAt: this.recordingStartedAt, pieces, audioPeaks, cutBlockIndexes: blockIndexes })
        const response: { server_id: string } = await api.post(`/medialibraries/screen-recordings/${recordingId}/publish`, { block_indexes: blockIndexes })
        if (recordingSession !== session) {
          return null
        }
        return response.server_id
      } catch {
        if (recordingSession === session) {
          this.error = "media-libraries.screenRecording.processingFailed"
          this.phase = "editing"
        }
        return null
      }
    },

    /**
     * Stops and clears the recording, also when the user switches to another app or account, which must neither see nor continue it.
     * Its uploaded pieces can still be recovered once the user is back in its app.
     */
    reset() {
      if (this.phase === "recording") {
        // The recorder would otherwise upload its last piece and open the editor
        mediaRecorder.ondataavailable = null
        mediaRecorder.onstop = null
        mediaRecorder.stop()
      }
      this.stopCapture()
      cutReencoder?.stop()
      cutReencoder = null
      releaseRecordingLock()
      URL.revokeObjectURL(this.previewUrl)
      chunks = []
      pendingBlocks = []
      uploadQueue = Promise.resolve()
      uploadUrl = null
      session++
      // Keeps the microphone choice
      const { recordMicrophone, microphoneId } = this
      this.$reset()
      this.$patch({ recordMicrophone, microphoneId })
    },

    // The saved recording belongs to the app that is selected when an uploader opens, and other tabs can change it
    refreshSavedRecording() {
      const savedRecording = loadSavedRecording()
      this.savedRecording = savedRecording && { recordedAt: savedRecording.recordedAt, duration: savedRecordingDuration(savedRecording) }
    },

    discardRecording() {
      forgetSavedRecording()
      this.reset()
    },
  },
  // The microphone choice stays the same for the next recording
  persist: {
    paths: ["recordMicrophone", "microphoneId"],
  },
})

if (import.meta.hot) {
  // @ts-ignore
  import.meta.hot.accept(acceptHMRUpdate(useScreenRecordingStore, import.meta.hot))
}
